import React, { Component } from "react";
import { Link } from "react-router-dom";
import ICreatePackingListDetailRowDTO from "../../../models/DTO/ICreatePackingListDetailRowDTO";
import ICreatePackingListsDTO from "../../../models/DTO/ICreatePackingListsDTO";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IOnCreatePackingListsDTO from "../../../models/DTO/IOnCreatePackingListsDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IAddress from "../../../models/IAddress";
import IOrder from "../../../models/IOrder";
import IPacking_List from "../../../models/IPacking_List";
import IPick_List from "../../../models/IPick_List";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import "./PackingListSidebar.scss";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwRadioSelect from "../../shared/OwRadioSelect/OwRadioSelect";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { Socket } from "socket.io-client";
import UIService from "../../../services/UIService";

enum PackingListOrderGroupEnum {
    GROUP_ORDERS_PER_VENDOR = "group_orders_per_vendor",
    ORDER_PER_PACKING_LIST = "order_per_packing_list",
}

interface vendorOrderGroupByAddressDetail {
    address: IAddress;
    orders: IOrder[];
    shipmentQty: number;
}

type Props = {
    pickList: IPick_List;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    socket: Socket;
    cancel: (refreshData?: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
};

type State = {
    orderGroupOption: PackingListOrderGroupEnum;
    oneOrderPerPackingListDetails: { [key: string]: { order: IOrder; shipmentQty: number } };
    groupOrdersPerVendorDetails: { [key: string]: vendorOrderGroupByAddressDetail[] };
    packingListOptions: { value: PackingListOrderGroupEnum; label: string }[];
    saveActive: boolean;
    savedPackingLists?: IPacking_List[];
};

class PackingListSidebar extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const packingListOptions: { value: PackingListOrderGroupEnum; label: string }[] = [
            {
                value: PackingListOrderGroupEnum.ORDER_PER_PACKING_LIST,
                label: "order_per_packing_list",
            },
        ];

        if (!this.props.supplierModel.one_packing_list_per_pick_list) {
            packingListOptions.unshift({
                value: PackingListOrderGroupEnum.GROUP_ORDERS_PER_VENDOR,
                label: "group_orders_per_customer",
            });
        }

        this.state = {
            orderGroupOption: PackingListOrderGroupEnum.ORDER_PER_PACKING_LIST,
            oneOrderPerPackingListDetails: {},
            groupOrdersPerVendorDetails: {},
            packingListOptions,
            saveActive: false,
        };
    }

    componentDidMount(): void {
        this.props.socket.on(EndpointsEnum.OrderOnCreatePackingLists, this.onCreatePackingLists);
        this.props.socket.on(EndpointsEnum.Notify, this.notify);
        this.buildPackingLists();
    }

    componentWillUnmount(): void {
        this.props.socket.removeListener(EndpointsEnum.Notify, this.notify);
    }

    notify = () => {
        this.setState({
            saveActive: false,
        });
    };

    cancel = () => {
        this.props.cancel(true);
    };

    handleChange = (e: any) => {
        // console.log("handleChange", e, e.target);
        const value = e.target.value;
        const name = e.target.name;

        const state: any = {
            [name]: value,
        };

        this.setState(state, () => {
            this.buildPackingLists();
        });
    };

    hasAddressCheck = (
        entryDetail: vendorOrderGroupByAddressDetail[],
        addressToCheck: IAddress
    ): { hasAddressAlready: boolean; addressIndex: number } => {
        let hasAddressAlready = false;
        let addressIndex = 0;

        for (const detail of entryDetail) {
            if (
                detail.address.address_line_one === addressToCheck.address_line_one &&
                detail.address.address_line_two === addressToCheck.address_line_two &&
                detail.address.address_line_three === addressToCheck.address_line_three &&
                detail.address.city === addressToCheck.city &&
                detail.address.state === addressToCheck.state &&
                detail.address.postal_code === addressToCheck.postal_code
            ) {
                hasAddressAlready = true;
                break;
            }

            addressIndex++;
        }

        return { hasAddressAlready, addressIndex };
    };

    buildPackingLists = () => {
        if (this.state.orderGroupOption === PackingListOrderGroupEnum.ORDER_PER_PACKING_LIST) {
            const shipmentGroupsShipmentQty: { [key: string]: { order: IOrder; shipmentQty: number } } = {};

            for (const order of this.props.pickList.Orders!) {
                shipmentGroupsShipmentQty[order.guid] = { order, shipmentQty: 0 };
            }

            this.setState({
                oneOrderPerPackingListDetails: shipmentGroupsShipmentQty,
            });
        } else {
            const groupOrdersPerVendorDetails: { [key: string]: vendorOrderGroupByAddressDetail[] } = {};

            for (const order of this.props.pickList.Orders!) {
                if (!order.Vendor) {
                    return null;
                }

                if (!groupOrdersPerVendorDetails[order.Vendor!.guid]) {
                    groupOrdersPerVendorDetails[order.Vendor!.guid] = [];
                }

                const hasAddressAlready = false;
                const addressIndex = 0;

                if (order.Ship_To) {
                    // need to see if this Ship To already exists
                    const details = this.hasAddressCheck(groupOrdersPerVendorDetails[order.Vendor.guid], order.Ship_To);

                    if (!details.hasAddressAlready) {
                        groupOrdersPerVendorDetails[order.Vendor.guid].push({ address: order.Ship_To, orders: [], shipmentQty: 0 });
                    }

                    groupOrdersPerVendorDetails[order.Vendor.guid][details.addressIndex].orders.push(order);
                } else {
                    const details = this.hasAddressCheck(groupOrdersPerVendorDetails[order.Vendor.guid], order.Vendor.Ship_To);

                    if (!details.hasAddressAlready) {
                        groupOrdersPerVendorDetails[order.Vendor.guid].push({ address: order.Vendor.Ship_To, orders: [], shipmentQty: 0 });
                    }

                    groupOrdersPerVendorDetails[order.Vendor.guid][details.addressIndex].orders.push(order);
                }
            }

            this.setState({
                groupOrdersPerVendorDetails,
            });
        }
    };

    handleShipmentQtyChange = (e: any) => {
        const oneOrderPerPackingListDetails = this.state.oneOrderPerPackingListDetails;

        oneOrderPerPackingListDetails[e.target.name].shipmentQty = Number(e.target.value);

        this.setState({
            oneOrderPerPackingListDetails,
        });
    };

    handleGroupedShipmentQtyChange = (e: any) => {
        const groupOrdersPerVendorDetails = this.state.groupOrdersPerVendorDetails;

        const name = e.target.name;
        const parsedName = name.split(",");

        const vendorGuid = parsedName[0];
        const addressDetailIndex = parsedName[1];

        groupOrdersPerVendorDetails[vendorGuid][addressDetailIndex].shipmentQty = Number(e.target.value);

        this.setState({
            groupOrdersPerVendorDetails,
        });
    };

    orderPerPackingListView = () => {
        const keys = Object.keys(this.state.oneOrderPerPackingListDetails);

        const output = keys.map((key) => {
            const detail = this.state.oneOrderPerPackingListDetails[key];

            let isReadOnly = false;

            if (this.props.supplierModel.one_packing_list_per_pick_list) {
                isReadOnly = true;
                detail.shipmentQty = 1;
            }

            return (
                <div key={key} className={"mt-4"}>
                    <div>
                        <b>{detail.order.name}</b>
                    </div>
                    <div>{detail.order.Vendor!.name}</div>
                    <div>
                        <OwLabel title={"no_of_shipments"} />{" "}
                        <OwInputWrapper
                            type={"number"}
                            min={0}
                            max={10}
                            name={key}
                            value={detail.shipmentQty}
                            fn={this.handleShipmentQtyChange}
                            disabled={isReadOnly}
                            onWheelFn={UIService.stopScrollWheel}
                        />{" "}
                    </div>
                </div>
            );
        });

        return output;
    };

    hasValidShipmentQty = (): boolean => {
        if (this.props.supplierModel.one_packing_list_per_pick_list) {
            // When one_packing_list_per_pick_list is true, shipmentQty is forced to 1
            // So validation always passes
            return true;
        }

        if (this.state.orderGroupOption === PackingListOrderGroupEnum.ORDER_PER_PACKING_LIST) {
            const keys = Object.keys(this.state.oneOrderPerPackingListDetails);
            return keys.some((key) => this.state.oneOrderPerPackingListDetails[key].shipmentQty >= 1);
        } else {
            const keys = Object.keys(this.state.groupOrdersPerVendorDetails);
            return keys.some((key) => this.state.groupOrdersPerVendorDetails[key].some((detail) => detail.shipmentQty >= 1));
        }
    };

    groupOrdersPerVendorView = () => {
        const keys = Object.keys(this.state.groupOrdersPerVendorDetails);

        const output = keys.map((key) => {
            const detail = this.state.groupOrdersPerVendorDetails[key];
            return (
                <div key={key} className={"mt-4"}>
                    <div>
                        <b>{detail[0].orders[0].Vendor!.name}</b>
                    </div>
                    {detail.map((addressEntryDetail, index) => {
                        const ordersList = addressEntryDetail.orders.map((order) => {
                            return <div key={order.guid}>{order.name}</div>;
                        });

                        return (
                            <div key={addressEntryDetail.address.guid}>
                                {ordersList}

                                <div>
                                    <AddressDisplay address={addressEntryDetail.address} />
                                </div>

                                <div>
                                    <OwLabel title={"no_of_shipments"} />
                                    <OwInputWrapper
                                        type={"number"}
                                        min={0}
                                        max={10}
                                        name={`${key},${index}`}
                                        value={addressEntryDetail.shipmentQty}
                                        fn={this.handleGroupedShipmentQtyChange}
                                        onWheelFn={UIService.stopScrollWheel}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        });

        console.log("orderPerPackingListView", output);

        return output;
    };

    save = () => {
        const packingLists: ICreatePackingListDetailRowDTO[] = [];

        if (this.state.orderGroupOption === PackingListOrderGroupEnum.ORDER_PER_PACKING_LIST) {
            const keys = Object.keys(this.state.oneOrderPerPackingListDetails);

            for (const key of keys) {
                const detailObj = this.state.oneOrderPerPackingListDetails[key];

                const detailRow: ICreatePackingListDetailRowDTO = {
                    num_shipments: detailObj.shipmentQty,
                    orders: [detailObj.order.guid],
                };

                packingLists.push(detailRow);
            }
        } else {
            const keys = Object.keys(this.state.groupOrdersPerVendorDetails);

            for (const key of keys) {
                const detailObj = this.state.groupOrdersPerVendorDetails[key];

                for (const detailObjRow of detailObj) {
                    const detailRow: ICreatePackingListDetailRowDTO = {
                        num_shipments: detailObjRow.shipmentQty,
                        orders: detailObjRow.orders.map((order) => {
                            return order.guid;
                        }),
                    };

                    packingLists.push(detailRow);
                }
            }
        }

        const payload: ICreatePackingListsDTO = {
            userGuid: this.props.userModel.guid,
            userToken: this.props.userModel.token,
            supplierGuid: this.props.supplierModel.guid,
            attachToExistingShipmentGroups: true,
            packingLists,
            pickListGuid: this.props.pickList.guid,
        };

        this.props.socket.emit(EndpointsEnum.OrderCreatePackingLists, payload);

        this.setState({
            saveActive: true,
        });
    };

    onCreatePackingLists = (data: IOnCreatePackingListsDTO) => {
        this.setState({
            saveActive: false,
            savedPackingLists: data.packingLists,
        });
    };

    render() {
        return (
            <div className="PackingListSidebar p-3">
                <div className={"header"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={this.cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle leftIconName={"far fa-book"} title={"create_packing_list"} />
                    <hr />
                </div>

                {this.state.savedPackingLists ? null : (
                    <div className={"d-block"}>
                        <div>
                            <OwTitle title={"packing_lists_order_grouping"} tag={"h5"} />

                            <OwRadioSelect
                                fn={this.handleChange}
                                supplierModel={this.props.supplierModel}
                                options={this.state.packingListOptions}
                                name={"orderGroupOption"}
                                defaultValue={this.state.orderGroupOption}
                            />
                        </div>

                        <div className={"mt-5"}>
                            <OwTitle title={"packing_lists"} tag={"h5"} />
                            {this.state.orderGroupOption === PackingListOrderGroupEnum.ORDER_PER_PACKING_LIST
                                ? this.orderPerPackingListView()
                                : this.groupOrdersPerVendorView()}
                        </div>

                        <div className={"mt-3"}>
                            <OwButton
                                title={"save"}
                                fn={this.save}
                                active={this.state.saveActive}
                                disableOnActive={true}
                                disabled={!this.hasValidShipmentQty()}
                            />
                        </div>
                    </div>
                )}

                {this.state.savedPackingLists ? (
                    <div>
                        <OwLabel title={"packing_lists_created"} />
                        <div>
                            <Link to={`/packing_lists?pick_list_name=${this.props.pickList.name}`} onClick={this.cancel}>
                                <OwLabel className={"pointer"} title={"view"} />
                            </Link>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default PackingListSidebar;
